<template>
  <transition name="strategy-product-intro">
    <div v-if="visible" class="strategy-product-intro" @click.self="close">
      <section class="strategy-product-intro__panel" role="dialog" aria-modal="true" :aria-labelledby="titleId">
        <header class="strategy-product-intro__header">
          <div>
            <h2 :id="titleId">{{ title }}</h2>
            <p>{{ tr('product_introduction') }}</p>
          </div>
          <button type="button" :aria-label="$t('关闭')" @click="close"><i class="el-icon-close" /></button>
        </header>

        <div class="strategy-product-intro__body">
          <template v-if="category === 'ai'">
            <article class="strategy-product-intro__section">
              <h3>UPlus-Astra / UPlus-Orbit</h3>
              <p v-for="key in aiDescriptionKeys" :key="key">{{ tr(key) }}</p>
              <p class="strategy-product-intro__warning">{{ tr('ai_quant_signal_risk_tip') }}</p>
              <div class="strategy-product-intro__support">
                <p>{{ tr('astra_supported_coins') }}</p>
                <p>{{ tr('orbit_supported_coins') }}</p>
              </div>
            </article>

            <article class="strategy-product-intro__section">
              <h3>UPlus-X / UPlus-X Pro</h3>
              <p v-for="key in xDescriptionKeys" :key="key">{{ tr(key) }}</p>
              <p>{{ tr('x_pro_supported_coins') }}</p>
              <p class="strategy-product-intro__warning">{{ tr('with_caution') }}</p>
              <h4>{{ tr('risk_warnings') }}</h4>
              <ul><li v-for="key in xRiskKeys" :key="key">{{ tr(key) }}</li></ul>
              <h4>{{ tr('product_logic') }}</h4>
              <ul><li v-for="key in xLogicKeys" :key="key">{{ tr(key) }}</li></ul>
              <ol class="strategy-product-intro__flow">
                <li v-for="key in xFlowKeys" :key="key">{{ tr(key) }}</li>
              </ol>
            </article>
          </template>

          <template v-else-if="category === 'hedge'">
            <article class="strategy-product-intro__section">
              <h3>{{ tr('Contract_product_positions') }}</h3>
              <p class="strategy-product-intro__note">{{ tr('Contract_product_positions_tip') }}</p>
              <PermissionMatrix :title="tr('Contract_product_use_rights')" :rows="permissionRows" />
            </article>
            <article class="strategy-product-intro__section">
              <h3>{{ tr('Contract_product_introduction') }}</h3>
              <p>{{ tr('contract_official_version_in') }}</p>
              <p>{{ tr('contract_speed_version_in') }}</p>
              <p v-for="key in contractDescriptionKeys" :key="key">{{ tr(key) }}</p>
              <p class="strategy-product-intro__warning">{{ tr('shall_prevail') }}</p>
            </article>
          </template>

          <template v-else-if="category === 'term'">
            <article class="strategy-product-intro__section">
              <h3>{{ tr('futures_spot_position') }}</h3>
              <p class="strategy-product-intro__note">{{ tr('Contract_product_positions_tip') }}</p>
              <PermissionMatrix :title="tr('futures_spot_use_rights')" :rows="permissionRows" />
            </article>
            <article class="strategy-product-intro__section">
              <h3>{{ tr('futures_spot_introduction') }}</h3>
              <p v-for="key in termDescriptionKeys" :key="key" class="preserve-lines">{{ tr(key) }}</p>
              <p class="strategy-product-intro__warning">{{ tr('shall_prevail') }}</p>
            </article>
          </template>

          <template v-else>
            <article class="strategy-product-intro__section">
              <h3>{{ tr('Permission_use_spot') }}</h3>
              <div class="strategy-product-intro__strategy-list">
                <div v-for="item in spotStrategies" :key="item.name">
                  <strong>{{ item.name }}</strong><span>{{ item.description }}</span>
                </div>
              </div>
              <PermissionMatrix :rows="permissionRows" />
            </article>
          </template>
        </div>

        <footer class="strategy-product-intro__footer">
          <button type="button" @click="close">{{ $tk('login.i_know') }}</button>
        </footer>
      </section>
    </div>
  </transition>
</template>

<script>
const PermissionMatrix = {
  name: 'StrategyPermissionMatrix',
  props: {
    title: { type: String, default: '' },
    rows: { type: Array, default: () => [] }
  },
  methods: {
    tk(key) { return typeof this.$tk === 'function' ? this.$tk(key) : this.$t(key) }
  },
  template: `
    <div class="strategy-permission">
      <h4 v-if="title">{{ title }}</h4>
      <div class="strategy-permission__table-wrap">
        <table class="strategy-permission__table">
          <thead>
            <tr><th rowspan="2"></th><th colspan="2">OKX</th><th colspan="2">Binance</th></tr>
            <tr>
              <th>{{ tk('robot.specify') }}</th><th>{{ tk('robot.no_specify') }}</th>
              <th>{{ tk('robot.specify') }}</th><th>{{ tk('robot.no_specify') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row">
              <th>{{ row }}</th>
              <td><i class="el-icon-check" /></td><td><i class="el-icon-check" /></td>
              <td><i class="el-icon-check" /></td><td><i class="el-icon-check" /></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  `
}

export default {
  name: 'StrategyProductIntroModal',
  components: { PermissionMatrix },
  props: {
    visible: { type: Boolean, default: false },
    category: { type: String, default: 'ai' },
    title: { type: String, default: '' }
  },
  data() {
    return {
      aiDescriptionKeys: ['ai_quant_signal_desc_1', 'ai_quant_signal_desc_2', 'ai_quant_signal_desc_3', 'ai_quant_signal_desc_4', 'ai_quant_signal_desc_5', 'ai_quant_signal_desc_6'],
      xDescriptionKeys: ['x_vip_tip', 'unidirectional_market', 'triggered', 'among_current'],
      xRiskKeys: ['insufficient_funds', 'reduce_positions', 'unrespons', 'program_problems', 'majeure_factors'],
      xLogicKeys: ['capital_utilization', 'stop_loss_mechanism', 'portfolio_risk_control', 'leverages'],
      xFlowKeys: ['data_collection', 'predicted', 'portfolio', 'automated_order', 'real_time'],
      contractDescriptionKeys: ['contract', 'stopped_out', 'stop_loss1', 'opposite_trend', 'rebound_exit'],
      termDescriptionKeys: ['futures_spot_introduction1', 'futures_spot_introduction2', 'futures_spot_introduction3']
    }
  },
  computed: {
    titleId() { return `strategy-product-intro-title-${this._uid}` },
    permissionRows() {
      if (this.category === 'hedge') return [this.tr('hedge')]
      if (this.category === 'term') return [this.tr('futures_spot_simple')]
      return [
        this.tr('Spot')
        // Spot grid and spot martin are temporarily hidden. Uncomment the next two lines to restore them.
        // this.tr('grid'),
        // this.tr('martin')
      ]
    },
    spotStrategies() {
      return [
        { name: this.tr('Spot_automatic_trading'), description: this.tr('spot_auto_tip1') }
        // Spot grid and spot martin are temporarily hidden. Uncomment the next two lines to restore them.
        // { name: this.tr('spot_grid'), description: this.tr('spot_grid_tip') },
        // { name: this.tr('spot_martin'), description: this.tr('spot_martin_tip') }
      ]
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (typeof document !== 'undefined') document.body.classList.toggle('strategy-product-intro-open', Boolean(value))
      }
    }
  },
  mounted() { window.addEventListener('keydown', this.handleKeydown) },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown)
    document.body.classList.remove('strategy-product-intro-open')
  },
  methods: {
    tr(key) { return this.$tk(`robot.${key}`) },
    close() { this.$emit('update:visible', false) },
    handleKeydown(event) { if (this.visible && event.key === 'Escape') this.close() }
  }
}
</script>

<style scoped>
.strategy-product-intro { position: fixed; z-index: 15200; inset: 0; display: flex; padding: 24px; box-sizing: border-box; align-items: center; justify-content: center; background: rgba(20, 17, 38, .46); }
.strategy-product-intro__panel { display: flex; width: min(780px, calc(100vw - 48px)); max-height: calc(100vh - 48px); box-sizing: border-box; flex-direction: column; overflow: hidden; border-radius: 16px; background: #fff; box-shadow: 0 24px 64px rgba(20, 16, 45, .28); }
.strategy-product-intro__header { display: flex; padding: 24px 26px 18px; align-items: flex-start; justify-content: space-between; gap: 16px; border-bottom: 1px solid #edf0f5; }
.strategy-product-intro__header h2, .strategy-product-intro__header p { margin: 0; }
.strategy-product-intro__header h2 { color: #151b2b; font-size: 20px; font-weight: 800; line-height: 28px; }
.strategy-product-intro__header p { margin-top: 3px; color: #98a0af; font-size: 13px; }
.strategy-product-intro__header button { padding: 4px; color: #98a0af; border: 0; background: transparent; font-size: 17px; cursor: pointer; }
.strategy-product-intro__body { min-height: 0; padding: 20px 26px 8px; overflow-y: auto; overscroll-behavior: contain; }
.strategy-product-intro__section { margin-bottom: 18px; }
.strategy-product-intro__section + .strategy-product-intro__section { padding-top: 20px; border-top: 1px solid #edf0f5; }
.strategy-product-intro__section h3 { margin: 0 0 12px; color: #151b2b; font-size: 16px; font-weight: 800; line-height: 24px; }
.strategy-product-intro__section h4 { margin: 16px 0 8px; color: #303747; font-size: 14px; font-weight: 800; line-height: 21px; }
.strategy-product-intro__section p { margin: 0 0 10px; color: #596174; font-size: 13px; line-height: 21px; }
.strategy-product-intro__section ul { margin: 0; padding-left: 20px; color: #596174; }
.strategy-product-intro__section li { margin-bottom: 6px; font-size: 13px; line-height: 20px; }
.strategy-product-intro__note { padding: 11px 13px; color: #496baf !important; border-radius: 9px; background: #f0f5fe; }
.strategy-product-intro__warning { padding: 11px 13px; color: #d94b52 !important; border-radius: 9px; background: #fff1f1; }
.strategy-product-intro__support { padding: 12px 14px 3px; border: 1px solid #dce7fb; border-radius: 10px; background: #f7f9fe; }
.strategy-product-intro__support p { color: #496baf; }
.preserve-lines { white-space: pre-line; }
.strategy-product-intro__flow { display: grid; margin: 12px 0 0; padding: 0; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 8px; list-style: none; counter-reset: flow; }
.strategy-product-intro__flow li { position: relative; margin: 0; padding: 28px 9px 10px; color: #496baf; border-radius: 9px; background: #f0f5fe; font-size: 11px; line-height: 17px; }
.strategy-product-intro__flow li::before { position: absolute; top: 8px; left: 9px; color: #1f6bff; content: counter(flow, decimal-leading-zero); counter-increment: flow; font-weight: 800; }
.strategy-product-intro__strategy-list { display: grid; margin-bottom: 14px; gap: 8px; }
.strategy-product-intro__strategy-list > div { display: flex; padding: 11px 13px; align-items: center; justify-content: space-between; gap: 16px; border: 1px solid #e7eaf1; border-radius: 9px; background: #fbfcfe; }
.strategy-product-intro__strategy-list strong { color: #303747; font-size: 13px; }
.strategy-product-intro__strategy-list span { color: #8a93a5; font-size: 12px; text-align: right; }
.strategy-product-intro__footer { padding: 14px 26px 22px; border-top: 1px solid #edf0f5; }
.strategy-product-intro__footer button { width: 100%; height: 42px; color: #fff; border: 0; border-radius: 8px; background: #1f6bff; font-family: inherit; font-size: 14px; font-weight: 700; cursor: pointer; }
.strategy-product-intro-enter-active, .strategy-product-intro-leave-active { transition: opacity .18s ease; }
.strategy-product-intro-enter-active .strategy-product-intro__panel, .strategy-product-intro-leave-active .strategy-product-intro__panel { transition: opacity .18s ease, transform .18s ease; }
.strategy-product-intro-enter, .strategy-product-intro-leave-to { opacity: 0; }
.strategy-product-intro-enter .strategy-product-intro__panel, .strategy-product-intro-leave-to .strategy-product-intro__panel { opacity: 0; transform: translateY(8px) scale(.99); }
::v-deep .strategy-permission { margin-top: 16px; }
::v-deep .strategy-permission h4 { margin: 0 0 9px; color: #303747; font-size: 14px; font-weight: 800; }
::v-deep .strategy-permission__table-wrap { overflow: hidden; border: 1px solid #e2e6ee; border-radius: 10px; }
::v-deep .strategy-permission__table { width: 100%; border-collapse: collapse; table-layout: fixed; }
::v-deep .strategy-permission__table th, ::v-deep .strategy-permission__table td { height: 34px; padding: 0 7px; text-align: center; border-right: 1px solid #e7eaf1; border-bottom: 1px solid #e7eaf1; font-size: 12px; }
::v-deep .strategy-permission__table tr:last-child th, ::v-deep .strategy-permission__table tr:last-child td { border-bottom: 0; }
::v-deep .strategy-permission__table th:last-child, ::v-deep .strategy-permission__table td:last-child { border-right: 0; }
::v-deep .strategy-permission__table thead th { color: #697386; background: #f5f7fa; font-weight: 700; }
::v-deep .strategy-permission__table tbody th { width: 20%; color: #303747; background: #fbfcfe; }
::v-deep .strategy-permission__table td { color: #13a66b; }
@media (max-width: 760px) {
  .strategy-product-intro { padding: 0; align-items: flex-end; overflow: hidden; }
  .strategy-product-intro__panel { position: relative; width: 100%; max-height: calc(100dvh - 12px); border-radius: 20px 20px 0 0; box-shadow: 0 -12px 36px rgba(16, 24, 40, .14); }
  .strategy-product-intro__panel::before { position: absolute; z-index: 1; top: 8px; left: 50%; width: 36px; height: 4px; border-radius: 2px; background: #dce1e9; content: ''; transform: translateX(-50%); }
  .strategy-product-intro__header { padding: 28px 18px 14px; }
  .strategy-product-intro__header h2 { font-size: 18px; line-height: 25px; }
  .strategy-product-intro__body { padding: 16px 18px 4px; }
  .strategy-product-intro__section p, .strategy-product-intro__section li { font-size: 12.5px; line-height: 20px; }
  .strategy-product-intro__flow { grid-template-columns: 1fr; }
  .strategy-product-intro__flow li { padding: 10px 10px 10px 39px; }
  .strategy-product-intro__flow li::before { top: 10px; }
  .strategy-product-intro__strategy-list > div { align-items: flex-start; flex-direction: column; gap: 3px; }
  .strategy-product-intro__strategy-list span { text-align: left; }
  .strategy-product-intro__footer { padding: 12px 18px calc(26px + env(safe-area-inset-bottom)); }
  ::v-deep .strategy-permission__table th, ::v-deep .strategy-permission__table td { padding: 0 3px; font-size: 10px; }
  .strategy-product-intro-enter-active .strategy-product-intro__panel, .strategy-product-intro-leave-active .strategy-product-intro__panel { transition: transform .28s cubic-bezier(.22, .8, .3, 1); }
  .strategy-product-intro-enter .strategy-product-intro__panel, .strategy-product-intro-leave-to .strategy-product-intro__panel { opacity: 1; transform: translateY(100%); }
}
</style>

<style>
body.strategy-product-intro-open { overflow: hidden; }
body.strategy-product-intro-open > .el-loading-mask,
body.strategy-product-intro-open .el-loading-mask.is-fullscreen { z-index: 15210 !important; }
</style>
